import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { AdvancedSection } from '@/core/AdvancedParameters.atom';
import { FormulaItemTable } from '@/tools/formula/FormulaItemTable.molecule';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { FeatureMappingConfig, FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.molecule';
import type { OutputBadges } from '@/tools/models/templateOutputStatus.utilities';
import type { IconDefinition } from '@seeqdev/icons';

export interface FormulaItemTableIF extends ValidatingFormComponent<string> {
  component: 'FormulaItemTableFormComponent';
  options: FormulaItemTableOption[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  collapseLabel?: string;
  collapseLabelClassNames?: string;
  collapseIconPosition?: 'left' | 'right';
  textEditable?: boolean;
  onTextChange?: (newValue: string, original: FormulaItemTableOption) => void;
  onEditClick?: (key: string) => void;
  onRemoveClick?: (key: string) => void;
  onAdditionalIconClick?: (key: string) => void;
  additionalIcon?: IconDefinition;
  additionalIconTooltip?: string;
  onSecondaryIconClick?: (key: string) => void;
  secondaryIcon?: string;
  secondaryIconTooltip?: string;
  hasErrors?: boolean;
  newLabel?: string;
  mappingConfig?: FeatureMappingConfig;
  /** Renders a checkbox column before each row's name when true. */
  selectable?: boolean;
  /** Keys of options whose checkbox is checked. Only meaningful when `selectable` is true. */
  selectedKeys?: Set<string>;
  /** Fires with the option's key and the checkbox's new checked state. */
  onSelectionChange?: (key: string, selected: boolean) => void;
  /** Fires with the new checked state when the Select All checkbox is toggled. */
  onSelectAllChange?: (selected: boolean) => void;
  /** Per-option badges to render, in order. An option absent from the map renders no badges at all. */
  outputBadges?: Map<string, OutputBadges>;
}

export const FormulaItemTableFormComponent = (props: FormulaItemTableIF) => {
  const { t } = useTranslation();

  const {
    options,
    extraClassNames,
    collapsible,
    defaultCollapsed = false,
    collapseLabel,
    collapseLabelClassNames,
    collapseIconPosition,
    tooltip,
    tooltipPlacement,
    tooltipDelay,
    testId,
    wrapperClassNames,
    textEditable,
    onTextChange,
    onEditClick,
    onRemoveClick,
    onAdditionalIconClick,
    additionalIcon,
    additionalIconTooltip,
    onSecondaryIconClick,
    secondaryIcon,
    secondaryIconTooltip,
    hasErrors = false,
    newLabel,
    mappingConfig,
    selectable,
    selectedKeys,
    onSelectionChange,
    onSelectAllChange,
    outputBadges,
  } = props;

  // expandSection is a signal for "force open due to validation errors"
  // Initial open/closed state is controlled by the collapsed prop
  const expandSection = hasErrors;

  const formulaItemTable = (
    <span
      {...stylex.props(props.xstyle)}
      className={clsx(stylex.props(props.xstyle).className, extraClassNames)}
      data-testid={testId}>
      <FormulaItemTable
        options={options}
        onEditClick={onEditClick}
        onRemoveClick={onRemoveClick}
        onAdditionalIconClick={onAdditionalIconClick}
        additionalIcon={additionalIcon}
        additionalIconTooltip={additionalIconTooltip}
        onSecondaryIconClick={onSecondaryIconClick}
        secondaryIcon={secondaryIcon}
        secondaryIconTooltip={secondaryIconTooltip}
        testId={testId}
        onTextChange={onTextChange}
        textEditable={textEditable}
        hasErrors={hasErrors}
        newLabel={newLabel}
        mappingConfig={mappingConfig}
        selectable={selectable}
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        onSelectAllChange={onSelectAllChange}
        outputBadges={outputBadges}
      />
    </span>
  );

  const content = collapsible ? (
    <div
      data-testid={`${testId}-collapsible`}
      {...stylex.props(props.wrapperXstyle)}
      className={clsx(stylex.props(props.wrapperXstyle).className, wrapperClassNames)}>
      <AdvancedSection
        collapsed={defaultCollapsed}
        alternateName={collapseLabel}
        labelClassNames={collapseLabelClassNames}
        labelXstyle={props.collapseLabelXstyle}
        iconPosition={collapseIconPosition}
        t={t}
        tooltip={tooltip}
        tooltipPlacement={tooltipPlacement}
        expandSection={expandSection}
        tooltipDelay={tooltipDelay}>
        {formulaItemTable}
      </AdvancedSection>
    </div>
  ) : (
    formulaItemTable
  );

  return <> {content} </>;
};
